import clsx from "clsx";
import { useState } from "react";

import { useQueryParams } from "../../hooks/useQueryParams";

const mappedLabel = (label: string): string => {
  return label === "&laquo; Previous"
    ? "Previous"
    : label === "Next &raquo;"
    ? "Next"
    : label;
};

export interface IPaginationProps {
  totalItems: number;
  itemsPerPage: number;
  pageIndex?: number;
}

interface IPaginationLink {
  label: string;
  label_key: string;
  page: number;
  disabled?: boolean;
}

const getPaginationLinks = (
  totalPages: number,
  currentPage: number,
): IPaginationLink[] => {
  const links: IPaginationLink[] = [];
  const buttonsAmount = 4;

  if (totalPages <= 1) {
    return links;
  }

  const generateLink = (label: string, page: number, disabled?: boolean) => ({
    label,
    label_key: label,
    page,
    disabled,
  });

  const addLinksInRange = (start: number, end: number) => {
    for (let i = start; i <= end; i++) {
      links.push(generateLink(i.toString(), i));
    }
  };

  const addDots = (keyPrefix?: string) => {
    links.push({
      label: "...",
      label_key: keyPrefix ? `${keyPrefix}...` : "...",
      disabled: false,
      page: -1,
    });
  };

  const addPreviousLink = () => {
    links.push(
      generateLink(
        "&laquo; Previous",
        currentPage - 1,
        currentPage === 1 || totalPages <= 2,
      ),
    );
  };

  const addNextLink = () => {
    links.push(
      generateLink(
        "Next &raquo;",
        currentPage + 1,
        currentPage === totalPages || totalPages <= 2,
      ),
    );
  };

  addPreviousLink();
  if (totalPages <= buttonsAmount + 1) {
    addLinksInRange(1, totalPages);
  } else {
    if (currentPage <= buttonsAmount) {
      addLinksInRange(1, buttonsAmount + 1);
      addDots();
      addLinksInRange(totalPages, totalPages);
    } else if (currentPage > totalPages - buttonsAmount) {
      addLinksInRange(1, 1);
      addDots();
      addLinksInRange(totalPages - buttonsAmount, totalPages);
    } else {
      addLinksInRange(1, 1);
      addDots();
      if (currentPage === totalPages) {
        addLinksInRange(currentPage - 2, currentPage);
      } else {
        addLinksInRange(currentPage - 1, currentPage + 1);
        addDots("1");
        addLinksInRange(totalPages, totalPages);
      }
    }
  }
  addNextLink();
  return links;
};

const getCurrentPage = (
  queryValue: string | null,
  pageIndex?: number,
): number => {
  if (pageIndex !== undefined) {
    return pageIndex + 1;
  }
  const page = queryValue;
  if (page && !isNaN(Number(page)) && page !== "0") {
    return Number(page);
  }
  return 1;
};

export const CustomizedPagination: React.FC<IPaginationProps> = ({
  totalItems,
  itemsPerPage,
  pageIndex,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const { queryValue, handleChange } = useQueryParams({
    queryStr: "p",
    onQueryChange: (val: string | null) => {
      handleUpdatePage(val ? Number(val) : 1);
    },
    onMountHandler: (val: string | null) => {
      handleUpdatePage(getCurrentPage(val));
    },
  });
  const [currentPage, setCurrentPage] = useState<number>(
    getCurrentPage(queryValue, pageIndex),
  );

  const handleUpdatePage = (page: number | null): void => {
    if (!page) return;
    setCurrentPage(page);
  };
  const handlePaginationChange = (page: number): void => {
    handleChange(String(page), true);
  };

  return (
    <div className="row">
      <div className="d-flex align-items-center justify-content-center justify-content-md-end custom-pagination">
        <div>
          <ul className="pagination">
            {getPaginationLinks(totalPages, currentPage)
              .map(link => ({
                ...link,
                label: mappedLabel(link.label),
                label_key: mappedLabel(link.label_key),
              }))
              .map(link => {
                return (
                  <li
                    key={link.label_key}
                    className={clsx("page-item", {
                      active: currentPage === link.page,
                      disabled: link.label === "...",
                      previous: link.label === "Previous",
                      next: link.label === "Next",
                      invisible: link.disabled,
                    })}
                  >
                    <span
                      className={clsx("page-link", {
                        "page-text":
                          link.label === "Previous" || link.label === "Next",
                        "me-5": link.label === "Previous",
                      })}
                      onClick={() => handlePaginationChange(link.page)}
                      style={{ cursor: "pointer" }}
                    >
                      {link.label}
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    </div>
  );
};
